// Shared, bounded wire contract for the full-screen Agent iframe.
const CHANNEL = 'nanofab.floating-chat';
const ID = /^req-[0-9a-f]{32}$/;
const INSTANCE = /^nfcb-[0-9a-f]{32}$/;
const keys = (v: any, allowed: string[]) => v && typeof v === 'object' && !Array.isArray(v)
    && Object.keys(v).every(key => allowed.includes(key));
const bytes = (v: any) => new TextEncoder().encode(JSON.stringify(v)).length;

export function validChatHostEnvelope(value: any, instanceId: string) {
    try {
        if (!keys(value, ['channel', 'version', 'type', 'requestId', 'instanceId', 'payload'])
            || value.channel !== CHANNEL || value.version !== 1 || value.instanceId !== instanceId
            || !ID.test(value.requestId || '') || bytes(value) > 65536) return false;
        const payload = value.payload;
        if (value.type === 'RESET') return keys(payload, []);
        if (value.type === 'QUERY') return keys(payload, ['query'])
            && typeof payload.query === 'string' && payload.query.trim().length > 0 && payload.query.length <= 2000;
        if (value.type === 'INIT' || value.type === 'HOST_CONTEXT') return keys(payload, ['context'])
            && keys(payload.context, Object.keys(payload.context || {})) && bytes(payload.context) <= 32768;
        return false;
    } catch (_error) { return false; }
}

export class ChatEmbedBridge {
    public ready = false;
    public authenticated = false;
    public context: any = {};
    private parentOrigin = '';
    private instanceId = '';
    private settled = false;
    private pending: MessageEvent | null = null;
    private seen = new Map<string, number>();
    private timer: any;
    private queryRequest = '';
    private destroyed = false;

    constructor(private callbacks: {
        query: (query: string) => Promise<void>, reset: () => Promise<void>,
        busy: () => boolean, changed: (message: string) => void
    }) {}

    public start() {
        const params = new URLSearchParams(location.search);
        const origin = params.get('parentOrigin') || '';
        const trusted = document.querySelector('meta[name="nanofab-chat-parent-origin"]')?.getAttribute('content');
        this.instanceId = params.get('instanceId') || '';
        try {
            const url = new URL(origin);
            if (!['http:', 'https:'].includes(url.protocol) || url.origin !== origin
                || origin !== trusted || !INSTANCE.test(this.instanceId) || window.parent === window) throw new Error();
        } catch (_error) {
            this.callbacks.changed('허용된 외부 플랫폼에서 AI Agent를 다시 열어 주세요.');
            return false;
        }
        this.parentOrigin = origin;
        window.addEventListener('message', this.receive);
        this.timer = window.setTimeout(() => {
            if (!this.ready) {
                this.callbacks.changed('외부 플랫폼 연결 시간이 초과되었습니다. 다시 열어 주세요.');
                this.destroy();
            }
        }, 8000);
        this.send('HELLO', null, { readyForInit: true, capabilities: this.capabilities() }, true);
        return true;
    }

    private capabilities() { return ['data_search', 'dataset_search', 'model', 'analysis', 'summary', 'visualization']; }

    public settle(authenticated: boolean) {
        if (this.destroyed) return;
        this.settled = true;
        this.authenticated = authenticated;
        if (this.pending) {
            const pending = this.pending;
            this.pending = null;
            void this.receive(pending);
        }
    }

    private receive = async (event: MessageEvent) => {
        if (this.destroyed || event.source !== window.parent || event.origin !== this.parentOrigin
            || !validChatHostEnvelope(event.data, this.instanceId)) return;
        const { type, requestId, payload } = event.data;
        if (type === 'INIT' && !this.settled) {
            if (!this.pending) this.pending = event;
            return;
        }
        if (type !== 'INIT' && !this.ready) return;
        const now = Date.now();
        for (const [key, timestamp] of this.seen) if (now - timestamp > 600000) this.seen.delete(key);
        const replay = type + ':' + requestId;
        if (this.seen.has(replay)) return;
        this.seen.set(replay, now);
        if (this.seen.size > 2048) this.seen.delete(this.seen.keys().next().value);
        if (type === 'INIT') {
            if (this.ready) return;
            this.ready = true;
            window.clearTimeout(this.timer);
            this.setContext(payload.context);
            this.send('ACK', requestId, { acknowledgedType: type });
            this.send('READY', requestId, { authenticated: this.authenticated, capabilities: this.capabilities() });
            this.callbacks.changed('');
        } else if (type === 'HOST_CONTEXT') {
            this.setContext(payload.context);
            this.send('ACK', requestId, { acknowledgedType: type });
        } else if (type === 'QUERY') {
            if (this.callbacks.busy()) {
                this.error('CHAT_BUSY', '진행 중인 질문을 완료하거나 새 대화를 시작해 주세요.', requestId);
                return;
            }
            this.queryRequest = requestId;
            this.send('ACK', requestId, { acknowledgedType: type });
            try { await this.callbacks.query(payload.query.trim()); }
            catch (_error) { this.error('QUERY_FAILED', '질문을 처리하지 못했습니다.', requestId); }
        } else if (type === 'RESET') {
            this.queryRequest = '';
            await this.callbacks.reset();
            this.send('ACK', requestId, { acknowledgedType: type });
        }
    };

    private setContext(context: any) {
        this.context = {};
        for (const key of ['path', 'title', 'selection']) {
            if (typeof context[key] === 'string') this.context[key] = context[key].slice(0, key === 'selection' ? 500 : 300);
        }
    }

    public requestId() {
        const values = new Uint8Array(16);
        window.crypto.getRandomValues(values);
        return 'req-' + Array.from(values).map(value => value.toString(16).padStart(2, '0')).join('');
    }

    public result(payload: any) {
        if (!this.ready) return;
        const requestId = this.queryRequest || this.requestId();
        this.queryRequest = '';
        this.send('RESULT', requestId, { ...payload, requestId });
    }

    public cancelPending() {
        if (this.queryRequest) this.error('QUERY_CANCELLED', '질문이 취소되었습니다.', this.queryRequest);
    }

    public openSearch(searchHandoff: any) {
        if (this.ready) this.send('OPEN_SEARCH', this.requestId(), { searchHandoff });
    }

    public error(code: string, message: string, requestId = '', retryable = true) {
        const correlation = requestId || this.queryRequest || this.requestId();
        this.send('ERROR', correlation, { code, message: message.slice(0, 500), retryable });
        if (correlation === this.queryRequest) this.queryRequest = '';
    }

    private send(type: string, requestId: string | null, payload: any, early = false) {
        if (this.destroyed || !this.parentOrigin || (!this.ready && !early)) return;
        const envelope = { channel: CHANNEL, version: 1, type, requestId, instanceId: this.instanceId, payload };
        if (bytes(envelope) <= 65536) window.parent.postMessage(envelope, this.parentOrigin);
    }

    public destroy() {
        this.destroyed = true;
        this.ready = false;
        this.pending = null;
        window.clearTimeout(this.timer);
        window.removeEventListener('message', this.receive);
        this.seen.clear();
    }
}

export function publicChatResult(result: any, question: string) {
    const text = (value: any, limit: number) => typeof value === 'string' ? value.slice(0, limit) : '';
    const list = (value: any, limit = 80) => Array.isArray(value)
        ? value.filter(item => typeof item === 'string').slice(0, 10).map(item => text(item, limit)) : [];
    const analysis = result?.analysis || {};
    const execution = result?.executionRequest || analysis.executionRequest || {};
    const intent = text(result?.intent || analysis.intent, 80);
    const retrieval = result?.retrieval;
    const resource = String(retrieval?.resourceType || analysis?.resourceType || '');
    const dataSearch = result?.source === 'bandi-api' || retrieval?.provider === 'bandi-api'
        || ['data.search', 'dataset.search'].some(name => (result?.actions || []).some((action: any) => action?.name === name));
    const output: any = {
        kind: dataSearch ? 'data' : 'answer', question: text(question, 1000),
        answer: text(result?.answer || result?.content || result?.reply, 8000),
        summary: text(result?.summary || result?.answer || result?.content || result?.reply, 1200),
        intent, dataScope: text(result?.dataScope, 60)
    };
    if (dataSearch && resource !== 'equipment') {
        const entities = execution.entities || analysis.entities || {};
        const periods = (Array.isArray(execution.periods) ? execution.periods : entities.periods || []);
        const dataset = resource === 'dataset';
        output.searchHandoff = {
            screen: dataset ? 'dataset-search' : 'detail-log-search', resourceType: dataset ? 'dataset' : 'data',
            query: text(analysis.resolvedQuery || execution.searchTerms?.[0] || question, 1000),
            filters: {
                lots: list(entities.lots), processes: list(entities.processes),
                equipment: list(entities.equipment, 120), parameters: list(entities.analysisItems || entities.parameters),
                periods: Array.isArray(periods) ? periods.slice(0, 4).map((p: any) => ({
                    key: text(p?.key, 40), label: text(p?.label, 80), start: text(p?.start, 10), end: text(p?.end, 10)
                })) : []
            }
        };
    }
    return output;
}
